import * as fernet from "fernet";

// Frontend env vars in CRA must be prefixed with REACT_APP_
const FERNET_KEY = process.env.REACT_APP_FERNET_KEY;

let secret = null;

if (FERNET_KEY) {
  try {
    secret = new fernet.Secret(FERNET_KEY);
  } catch (e) {
    // If the key is misconfigured, encryption will be a no-op
    // but we avoid breaking the UI.
    console.error("Invalid REACT_APP_FERNET_KEY for Fernet encryption:", e);
    secret = null;
  }
} else {
  console.warn("REACT_APP_FERNET_KEY is not set; encryption will be skipped.");
}

/**
 * Encrypt a string using Fernet.
 * Returns the encrypted token string, or the original value if encryption
 * is not available or fails. This keeps the app functional while
 * surfacing issues via console logs.
 */
export function encryptString(value) {
  if (!value && value !== "") return value;

  if (!secret) {
    return value;
  }

  try {
    const token = new fernet.Token({
      secret,
      // Use current time for standard Fernet semantics
      time: Date.now(),
    });

    return token.encode(String(value));
  } catch (e) {
    console.error("Fernet encryption failed; sending plaintext instead:", e);
    return value;
  }
}

/**
 * Helper to encrypt an arbitrary payload object as JSON.
 * Returns an encrypted token string (or the original JSON string on failure).
 */
export function encryptPayload(payload) {
  const json = JSON.stringify(payload ?? {});
  return encryptString(json);
}

/** True if Fernet encryption is available (REACT_APP_FERNET_KEY set and valid). */
export function isEncryptionAvailable() {
  return secret !== null;
}

/**
 * Build request body for JSON endpoints per backend spec:
 * - If encryption is available: { data: "<fernet-token>" }
 * - Otherwise: plain payload (backend treats as normal JSON)
 */
export function buildJsonBody(payload) {
  const p = payload ?? {};
  if (isEncryptionAvailable()) {
    const rawJson = JSON.stringify(p);
    const encrypted = encryptPayload(p);

    // If encryption fails, encryptPayload falls back to plaintext.
    // In that case, keep sending normal JSON instead of { data: "<plaintext-json>" }.
    if (typeof encrypted === "string" && encrypted !== rawJson) {
      return { data: encrypted };
    }
  }
  return p;
}

